/**
 * StyleSelector — style pill trigger + dropdown grid panel.
 *
 * Self-contained: owns open/close state, reads image style from configStore,
 * and emits style changes via socketStore.
 */

import React, { useState } from "react";
import { View, Text, Pressable, Image, Modal } from "react-native";
import { styles } from "./StoryView.styles";
import { colors, useScale, design } from "../../styles";
import { useConfigStore, IMAGE_STYLE_OPTIONS } from "../../store/configStore";
import { useSocketStore } from "../../store/socketStore";
import { useStoryStore } from "../../store/storyStore";
import { trackButton, Button, View as MetricView } from "../../services/metrics";
import STYLE_PREVIEWS from "./stylePreviews";

interface StyleSelectorProps {
  onOpen?: () => void;
  disabled?: boolean;
}

export default function StyleSelector({ onOpen, disabled }: StyleSelectorProps) {
  const [open, setOpen] = useState(false);
  const { imageStyle, setImageStyle } = useConfigStore();
  const emitEvent = useSocketStore((s) => s.emitEvent);
  const { s } = useScale();

  const currentLabel =
    IMAGE_STYLE_OPTIONS.find((o) => o.value === imageStyle)?.label ?? imageStyle;

  const handleChange = (value: string) => {
    setImageStyle(value);
    emitEvent("syncImageStyle", { imageStyle: value });
    const { characters, scenes, updateCharacter, updateScene } = useStoryStore.getState();
    if (characters.length > 0 || scenes.length > 0) {
      characters.forEach((c) => updateCharacter(c.characterId, { isLoading: true, imageBase64: undefined }));
      scenes.forEach((sc) => updateScene(sc.sceneId, { isLoading: true, imageBase64: undefined }));
      emitEvent("regenerateStoryImages");
    }
    setOpen(false);
  };

  const toggle = () => {
    if (!open) {
      onOpen?.();
      trackButton(MetricView.StoryBuilder, Button.StoryBuilder.StylePicker);
    }
    setOpen(!open);
  };

  const preview = STYLE_PREVIEWS[imageStyle];

  return (
    <>
      {/* Pill trigger */}
      <Pressable style={[styles.stylePill, { height: s(design.stylePill.height), minWidth: s(design.stylePill.minWidth), borderRadius: s(design.stylePill.radius) }, disabled && { opacity: 0.4 }]} onPress={toggle} disabled={disabled}>
        {preview ? (
          <View style={[styles.stylePillPreview, { width: s(design.stylePill.previewSize), height: s(design.stylePill.previewSize), borderRadius: s(design.stylePill.radius) }]}>
            <Image
              source={typeof preview === "string" ? { uri: preview } : preview}
              style={{ width: s(design.stylePill.previewSize), height: s(design.stylePill.previewSize) }}
              resizeMode="cover"
              resizeMethod="resize"
            />
          </View>
        ) : null}
        <View>
          <Text style={[styles.stylePillLabel, { fontSize: s(design.stylePillLabel.fontSize) }]}>Style:</Text>
          <Text style={[styles.stylePillValue, { fontSize: s(design.stylePillValue.fontSize) }]}>{currentLabel}</Text>
        </View>
      </Pressable>

      {/* Dropdown */}
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)} />
        <View style={[styles.styleDropdown, { top: s(design.styleGrid.dropdownTop), paddingRight: s(design.styleGrid.dropdownPaddingRight) }]} pointerEvents="box-none">
          <View style={[styles.styleDropdownInner, { borderRadius: s(design.styleGrid.radius), padding: s(design.styleGrid.padding), marginTop: s(design.styleGrid.gap) }]}>
            <View style={[styles.styleGrid, { width: s(design.styleGrid.itemSlot) * design.styleGrid.columns }]}>
              {IMAGE_STYLE_OPTIONS.map((opt) => {
                const isActive = imageStyle === opt.value;
                const optPreview = STYLE_PREVIEWS[opt.value];
                const imgSize = s(design.styleGrid.imgSize);
                const borderW = s(design.styleGrid.borderW);
                const radius = s(design.styleGrid.radius);
                const margin = s(design.styleGrid.margin);
                return (
                  <Pressable key={opt.value} style={[styles.styleOption, { width: imgSize, margin }]} onPress={() => handleChange(opt.value)}>
                    <View style={[styles.stylePreviewClip, { width: imgSize, height: imgSize, borderRadius: radius }]}>
                      {optPreview ? (
                        <Image source={typeof optPreview === "string" ? { uri: optPreview } : optPreview} style={{ width: imgSize, height: imgSize }} resizeMode="cover" resizeMethod="resize" />
                      ) : (
                        <View style={[styles.stylePreviewPlaceholder, { width: imgSize, height: imgSize, borderRadius: radius }]} />
                      )}
                      {isActive && <View style={{ position: "absolute", top: 0, left: 0, width: imgSize, height: imgSize, borderRadius: radius, borderWidth: borderW, borderColor: colors.styleSelectedBorder }} />}
                    </View>
                    <Text style={[styles.styleOptionText, { fontSize: s(design.styleGrid.fontSize), marginTop: s(design.styleGrid.margin) }]}>{opt.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}
